import { BITE_STATUS } from "@/features/tutor/store/useBiteStatusStore";
import { startTutorTurn } from "@/features/tutor/utils/tutorTurnLifecycle";
import { isNotepadSurface } from "@/features/notepad/utils/teachingSurface";

export const resolveTargetSlideIndex = (currentIndex, newIndex) =>
  newIndex !== undefined ? newIndex : currentIndex + 1;

export const isDuplicateSlideChange = (
  pendingRequest,
  sessionId,
  targetIndex,
) =>
  Boolean(
    pendingRequest &&
      pendingRequest.sessionId === sessionId &&
      pendingRequest.targetIndex === targetIndex,
  );

const defaultDispatchSlideChangeError = ({ sessionId, targetIndex, error }) => {
  if (typeof window === "undefined") {
    return;
  }

  window.dispatchEvent(
    new CustomEvent("vodemo:slide-change-error", {
      detail: {
        sessionId,
        targetIndex,
        error: String(error),
      },
    }),
  );
};

const defaultDispatchCachedBiteEntry = ({
  sessionId,
  slideIndex,
  cachedBite,
}) => {
  if (typeof window === "undefined") {
    return;
  }

  window.dispatchEvent(
    new CustomEvent("vodemo:cached-bite-entry", {
      detail: {
        sessionId,
        slideIndex,
        cachedBite,
      },
    }),
  );
};

export function createSlideNavigationController({
  get,
  set,
  tutorApi,
  biteStatusStore,
  notepadStore,
  notepadPerSlideStore,
  debugLog = () => {},
  debugWarn = () => {},
  dispatchSlideChangeError = defaultDispatchSlideChangeError,
  dispatchCachedBiteEntry = defaultDispatchCachedBiteEntry,
}) {
  const changeSlide = (sessionId, newIndex, skipReason = null) => {
    const state = get();
    const currentSlides = state.slides;
    const currentIndex = state.currentSlideIndex;
    const currentDeck = state.currentDeck;

    if (!currentSlides || currentSlides.length === 0) {
      debugLog("No slides available, skipping toggle");
      return undefined;
    }

    const targetIndex = resolveTargetSlideIndex(currentIndex, newIndex);

    if (newIndex === undefined) {
      // `currentIndex` is written optimistically below, so a relative target
      // can measure against an index another navigation has already moved.
      debugWarn(
        `handleChangeSlide called without an explicit slide index (resolved ${currentIndex} -> ${targetIndex})`,
      );
    }

    if (targetIndex === currentIndex) {
      debugLog("Already on slide, skipping toggle");
      return undefined;
    }

    if (
      targetIndex !== undefined &&
      (!Number.isInteger(targetIndex) ||
        targetIndex < 0 ||
        targetIndex >= currentSlides.length)
    ) {
      debugLog("Reached the end of the slides, skipping toggle");
      return undefined;
    }

    const existingPending = state.pendingSlideChangeRequest;
    const fromIndex = Number.isInteger(currentIndex) ? currentIndex : -1;

    if (isDuplicateSlideChange(existingPending, sessionId, targetIndex)) {
      debugLog(
        `handleChangeSlide already in-flight for session ${sessionId}, target ${targetIndex}`,
      );
      return undefined;
    }

    debugLog(`handleChangeSlide called ${currentIndex} -> ${targetIndex}`);
    const requestToken = (state.slideChangeRequestToken || 0) + 1;
    const { setStatus, statuses } = biteStatusStore.getState();
    const existingStatus = fromIndex >= 0 ? statuses[fromIndex] : null;
    const navigatedBy = skipReason === "user" ? "user" : "ai";
    // Only the tutor's NextSlide passes "ai" explicitly; other callers fall back
    // to navigatedBy "ai" but announced nothing in its history.
    const isTutorAnnounced = skipReason === "ai";
    const activeNotepadStore = notepadStore.getState();

    if (
      currentDeck &&
      Number.isInteger(fromIndex) &&
      typeof currentDeck.getIndices === "function"
    ) {
      const indices = currentDeck.getIndices();
      const fragmentIndex = Number.isInteger(indices?.f) ? indices.f : -1;
      set((previous) => ({
        slideFragmentMemory: {
          ...(previous.slideFragmentMemory || {}),
          [fromIndex]: fragmentIndex,
        },
      }));
    }

    const isCurrentNotepadBite = activeNotepadStore.isNotepadBite;
    const targetSlide = currentSlides.find(
      (s) => s?.slide_index === targetIndex,
    );
    const isTargetNotepadBite = isNotepadSurface(targetSlide?.metadata);

    if (isCurrentNotepadBite && isTargetNotepadBite) {
      // Bite-to-bite: keep notepad open, just reset pages.
      // Transport reconnects via TldrawCanvasAgent key change on new slide.
      activeNotepadStore.resetPages();
    } else {
      activeNotepadStore.close();
      activeNotepadStore.resetPages();
    }

    const optimisticStatus =
      existingStatus === BITE_STATUS.COMPLETED
        ? BITE_STATUS.COMPLETED
        : BITE_STATUS.PROCESSING;
    if (fromIndex >= 0) {
      setStatus(fromIndex, optimisticStatus);
    }

    set((previous) => ({
      currentSlideIndex: targetIndex,
      slideActionCount: 0,
      hasFailedAiSlideChange: false,
      slideChangeRequestToken: requestToken,
      pendingSlideChangeRequest: {
        token: requestToken,
        sessionId,
        targetIndex,
        fromIndex,
        previousStatus: existingStatus,
      },
    }));

    if (currentDeck && typeof currentDeck.slide === "function") {
      currentDeck.slide(targetIndex);
    }

    const syncSlideChange = async () => {
      let response;
      // The backend starts the next slide's opener off this request, and no
      // TURN_CHANGE precedes it, so a pending toggle has to land here or that
      // opener runs under the previous mode.
      try {
        response = await startTutorTurn((tutor_audio_on) =>
          tutorApi.post(`/session/change_slide/${sessionId}`, {
            slide_index: targetIndex,
            navigated_by: navigatedBy,
            tutor_audio_on,
          }),
        );
      } catch (error) {
        const latestRequest = get().pendingSlideChangeRequest;
        if (!latestRequest || latestRequest.token !== requestToken) {
          return;
        }

        debugWarn(
          `Error changing slide (session=${sessionId}, target=${targetIndex}): ${error}`,
        );
        dispatchSlideChangeError({
          sessionId,
          targetIndex,
          error,
        });

        const restoreIndex = latestRequest.fromIndex;
        set((previous) => ({
          currentSlideIndex: restoreIndex,
          slideActionCount: 0,
          hasFailedAiSlideChange: isTutorAnnounced,
          pendingSlideChangeRequest:
            previous.pendingSlideChangeRequest?.token === requestToken
              ? null
              : previous.pendingSlideChangeRequest,
        }));

        if (restoreIndex >= 0 && latestRequest.previousStatus !== null) {
          biteStatusStore
            .getState()
            .setStatus(restoreIndex, latestRequest.previousStatus);
        }
        return;
      }

      const latestRequest = get().pendingSlideChangeRequest;
      if (!latestRequest || latestRequest.token !== requestToken) {
        return;
      }

      const confirmedIndex = response.data.slide_index;
      const cachedBite = response.data?.cached_bite;
      const backendRehydratePayload = response.data?.rehydrate_payload;
      const backendFragmentIndex = Number.isInteger(
        backendRehydratePayload?.fragment_index,
      )
        ? backendRehydratePayload.fragment_index
        : null;
      debugLog("Backend confirmed slide change to:", confirmedIndex);

      // Validate the confirmed index against the lesson's slides array.
      // NOTE: Do NOT use currentDeck.getTotalSlides() here — that returns the
      // number of <section> elements inside the current slide's Reveal.js HTML
      // (always 1 per slide file), not the number of lesson-level slides.
      // Using the deck count caused every transition beyond slide 0 to be
      // incorrectly rejected and rolled back.
      const lessonSlides = get().slides ?? [];
      if (
        !Number.isInteger(confirmedIndex) ||
        confirmedIndex < 0 ||
        (lessonSlides.length > 0 && confirmedIndex >= lessonSlides.length)
      ) {
        debugWarn(
          `Invalid slide index: ${confirmedIndex}. Lesson slide count: ${lessonSlides.length}`,
        );
        const restoreIndex = latestRequest.fromIndex;
        set((previous) => ({
          currentSlideIndex: restoreIndex,
          slideActionCount: 0,
          pendingSlideChangeRequest:
            previous.pendingSlideChangeRequest?.token === requestToken
              ? null
              : previous.pendingSlideChangeRequest,
        }));
        if (restoreIndex >= 0 && latestRequest.previousStatus !== null) {
          biteStatusStore
            .getState()
            .setStatus(restoreIndex, latestRequest.previousStatus);
        }
        return;
      }

      const notepadUsedOnSlide = backendRehydratePayload?.notepad_used === true;
      notepadPerSlideStore
        .getState()
        .syncSlide(confirmedIndex, notepadUsedOnSlide);

      set((previous) => ({
        currentSlideIndex: confirmedIndex,
        slideActionCount: 0,
        slideFragmentMemory:
          backendFragmentIndex !== null
            ? {
                ...(previous.slideFragmentMemory || {}),
                [confirmedIndex]: backendFragmentIndex,
              }
            : previous.slideFragmentMemory,
        pendingSlideChangeRequest:
          previous.pendingSlideChangeRequest?.token === requestToken
            ? null
            : previous.pendingSlideChangeRequest,
      }));

      const { bite_status: biteStatus } = response.data;
      if (biteStatus && fromIndex >= 0) {
        biteStatusStore.getState().setStatus(fromIndex, biteStatus);
      }

      dispatchCachedBiteEntry({
        sessionId,
        slideIndex: confirmedIndex,
        cachedBite: cachedBite ?? { has_cached_state: false },
      });

      // RevealRenderer owns applying slideFragmentMemory to the active local deck.
      // This keeps backend checkpoint fragments reactive when they arrive after
      // the target slide has already rendered its default state.
    };

    const syncPromise = syncSlideChange();
    void syncPromise;
    return syncPromise;
  };

  return {
    changeSlide,
  };
}
